import { useDeliveredShipments, useSearchShipments, useShipments } from '@/modules/pmt/hooks';
import type { IShipment } from '@/modules/pmt/interfaces';
import {
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useShipmentMerchantInfo } from '../hooks/useShipmentMerchantInfo';
import { useShipmentFiltersContext } from './ShipmentFilters.context';

interface IShipmentsOverviewContext {
  allShipments: IShipment[];

  // overview
  shipments: IShipment[];

  /**
   * No shipments in an account
   */
  noShipmentsAdded: boolean;
  /**
   * No shipments found for the current filter
   */
  noShipmentsFound: boolean;
  /**
   * If there are more shipments to load
   */
  hasMoreShipments: boolean;
  /**
   * Total number of shipments at first load PMT
   * This is currently used for GA4 only (click_filter, click_sort)
   */
  shipmentsQty: number | null;
  shipmentsLoaded: boolean;
  setShipmentsLoaded: Dispatch<SetStateAction<boolean>>;

  // delivered
  deliveredShipments: IShipment[];
  deliveredQueryLimit: number;
  hasMoreDelivered: boolean;
  fetchMoreDelivered: () => void;
  noDeliveredShipmentsFound: boolean;

  setShipments: Dispatch<SetStateAction<{ overview: IShipment[]; delivered: IShipment[] }>>;
  deletingShipmentsRef: MutableRefObject<Set<string>>;

  /**
   * Flag to auto-open shipment details on mobile for single shipment submissions
   */
  shouldAutoOpenMobileDetails: boolean;
  setShouldAutoOpenMobileDetails: Dispatch<SetStateAction<boolean>>;
}

const defaultState = {
  allShipments: [],
  shipments: [],
  noShipmentsAdded: true,
  noShipmentsFound: true,
  hasMoreShipments: false,
  shipmentsQty: null,
  shipmentsLoaded: false,
  setShipmentsLoaded: () => null,
  deliveredShipments: [],
  deliveredQueryLimit: 3,
  hasMoreDelivered: false,
  fetchMoreDelivered: () => null,
  noDeliveredShipmentsFound: true,
  setShipments: () => null,
  deletingShipmentsRef: { current: new Set<string>() },
  shouldAutoOpenMobileDetails: false,
  setShouldAutoOpenMobileDetails: () => null,
};

const ShipmentsOverviewContext = createContext<IShipmentsOverviewContext>(defaultState);

export const ShipmentsOverviewProvider = ({ children }: { children: ReactNode }) => {
  const [shipments, setShipments] = useState<{ overview: IShipment[]; delivered: IShipment[] }>({
    overview: [],
    delivered: [],
  });

  const [shouldAutoOpenMobileDetails, setShouldAutoOpenMobileDetails] = useState(false);

  const { filterConfig } = useShipmentFiltersContext();

  const deletingShipmentsRef = useRef<Set<string>>(new Set());

  const { shipmentsQty, noShipmentsAdded, noShipmentsFound, hasMoreShipments, shipmentsLoaded, setShipmentsLoaded } =
    useShipments(deletingShipmentsRef, shipments, setShipments);

  const allShipments = useMemo(() => [...shipments.delivered, ...shipments.overview], [shipments]);

  const {
    queryLimit: deliveredQueryLimit,
    fetchMore: fetchMoreDelivered,
    noDeliveredShipmentsFound,
    hasMore: hasMoreDelivered,
  } = useDeliveredShipments(deletingShipmentsRef, shipments, setShipments);

  // Check query params to search shipments in cases:
  // -- user search shipments from PMC --> PMT
  // -- user search shipments from AMP --> PMT
  // -- user click on shipment in email --> PMT
  useSearchShipments();

  // Fetch merchant info for shipments
  useShipmentMerchantInfo({
    shipments,
    shipmentsLoaded,
    setShipments,
  });

  const ctxValue = useMemo(
    () => ({
      allShipments,
      // overview
      shipments: shipments.overview.slice(0, filterConfig.currentLimit),
      noShipmentsAdded,
      noShipmentsFound,
      hasMoreShipments,
      shipmentsQty,
      shipmentsLoaded,
      setShipmentsLoaded,
      // delivered
      deliveredShipments: shipments.delivered.slice(0, deliveredQueryLimit),
      deliveredQueryLimit,
      hasMoreDelivered,
      fetchMoreDelivered,
      noDeliveredShipmentsFound,
      //
      setShipments,
      deletingShipmentsRef,
      shouldAutoOpenMobileDetails,
      setShouldAutoOpenMobileDetails,
    }),
    [
      allShipments,
      shipments.overview,
      shipments.delivered,
      filterConfig.currentLimit,
      noShipmentsAdded,
      noShipmentsFound,
      hasMoreShipments,
      shipmentsQty,
      shipmentsLoaded,
      setShipmentsLoaded,
      deliveredQueryLimit,
      hasMoreDelivered,
      fetchMoreDelivered,
      noDeliveredShipmentsFound,
      shouldAutoOpenMobileDetails,
    ],
  );

  return <ShipmentsOverviewContext.Provider value={ctxValue}>{children}</ShipmentsOverviewContext.Provider>;
};

export const useShipmentsOverviewContext = () => useContext(ShipmentsOverviewContext);
